import { Component, OnDestroy, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { Router } from '@angular/router';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { Subject, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged, switchMap } from 'rxjs/operators';
import { Company, User } from '../../../auth/models';
import { ConfirmModalComponent } from "../../../common/components";
import { ToastService } from '../../../main/services/toast';
import { TenderImportModalComponent } from '../../components/tender-import-modal/tender-import-modal.component';
import { TenderXmlImportsService, TendersService } from '../../services';

@Component({
    selector: 'jft-tenders',
    templateUrl: './tenders.component.html',
    styleUrls: ['./tenders.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class TendersComponent implements OnInit, OnDestroy {

    public order_field: string = null;
    public order: string = null;
    public isCollapsed: boolean = true;
    private termObservable = new Subject();
    private termSubscription: Subscription;

    constructor(
        private router: Router,
        private service: TendersService,
        private importXmlService: TenderXmlImportsService,
        private toast: ToastService,
        public user: User,
        public company: Company,
        private modalService: NgbModal
    ) {
    }

    public get canImportContracts(): boolean {
        if (this.company?.isChild || this.user.isAnyRestricted()) return false;
        return this.user.canManageAdvanced();
    }

    public ngOnInit() {
        // No default column sort: the resolver has already fetched the list in
        // the server's default order (active first, then drafts, then past).
        // Claiming a column here would put a chevron over data sorted by
        // something else, since nothing refetches on init.
        this.termSubscription = this.termObservable.pipe(
            debounceTime(500),
            distinctUntilChanged(),
            switchMap((term: string) => {
                return this.service.setTerm(term).fetch();
            })
        ).subscribe(() => {

        }, (e) => {
            this.toast.error(e);
        });
    }

    public sort($event: Event, field: string) {
        $event.preventDefault();
        $event.stopPropagation();
        if (this.order_field == field) {
            this.order = this.order == 'ASC' ? 'DESC' : 'ASC';
        } else {
            this.order = 'ASC';
        }
        this.order_field = field;
        this.service.setOrder(this.order_field, this.order).fetch().subscribe(() => {

        }, (e) => {
            this.toast.error(e);
        });
    }

    public search(term) {
        this.termObservable.next(term);
    }

    public importXml(files: File[]) {
        //this.importXmlService.getXmlSource(files[0]);  todo
        const modalRef = this.modalService.open(ConfirmModalComponent);
        modalRef.componentInstance.title = 'Tender XML';
        modalRef.componentInstance.text = 'Are you sure you want to upload XML file?';

        modalRef.result.then((result) => {
            if (result === true) {
                this.confirmImportXml(files);
            }
        });
    }

    private confirmImportXml(files: File[]) {
        this.isCollapsed = true;
        this.importXmlService.importXml(files[0]).subscribe((results) => {
            this.service.fetch().subscribe();
            this.toast.success(results['message'], 'Tender XML');
        }, (e) => {
            this.toast.error(e);
        });
    }

    public openContractImporterModal() {
        const modalRef = this.modalService.open(TenderImportModalComponent, {size: 'lg', backdrop: 'static'});
        modalRef.result.then(() => {
            this.service.fetch().subscribe();
        });
    }

    public ngOnDestroy() {
        this.termSubscription.unsubscribe();
        // Leaving the list clears the sort and search, so coming back lands on
        // the default order again. Switching between the All/Active/Past/Draft
        // tabs keeps this component alive and therefore keeps the sort.
        this.service.reset();
    }
}
